import React from 'react';
import { createPortal } from 'react-dom';

function placeFloatingPanel(rect: DOMRect, width: number, height: number, gap = 6) {
  const margin = 8;
  const left = Math.min(Math.max(rect.left, margin), Math.max(margin, window.innerWidth - width - margin));
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const opensAbove = spaceBelow < height + gap && spaceAbove > spaceBelow;
  const rawTop = opensAbove ? rect.top - height - gap : rect.bottom + gap;
  const top = Math.min(Math.max(rawTop, margin), Math.max(margin, window.innerHeight - height - margin));
  return { left, top };
}

export type OfferFloatingEditShellProps = {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  panelWidth?: number;
  ariaLabel?: string;
};

/** Portals edit UI over the document so the offer preview layout does not shift. */
export function OfferFloatingEditShell({
  open,
  onClose,
  anchorRef,
  children,
  panelWidth = 300,
  ariaLabel = 'Bearbeiten',
}: OfferFloatingEditShellProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null);

  const updatePosition = React.useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!rect) return;
    const height = panel?.offsetHeight ?? 240;
    const width = panel?.offsetWidth ?? panelWidth;
    setPosition(placeFloatingPanel(rect, width, height));
  }, [anchorRef, panelWidth]);

  React.useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
    const panel = panelRef.current;
    const ro =
      panel && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => updatePosition())
        : null;
    if (panel && ro) ro.observe(panel);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Offene Auswahlliste (Jahr/KW, Zahlungsziel …) schließt zuerst nur sich selbst.
      if (document.querySelector('[data-app-select-list]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/*
        Klickfänger für „außerhalb klicken schließt“. Muss über dem Editor liegen (fixed z-250),
        sonst landet der Klick im Dokument. Durchsichtig, damit der Editor nicht abgedunkelt wird.
      */}
      <div className="fixed inset-0 z-[255]" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className="fixed z-[256] rounded-md border border-app-border bg-app-surface p-4 text-app-text shadow-2xl"
        style={{
          left: position?.left ?? 16,
          top: position?.top ?? 16,
          width: panelWidth,
          maxWidth: 'min(100vw - 16px, 420px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
